import { useEffect, useState } from 'react';

import { DateRange } from 'react-day-picker';

import { CellContext, type ColumnDef, type Row } from '@tanstack/react-table';
import {
  type FormikProps,
  ScheduleTimeFrame,
  type TimeFrameObject
} from 'common/contracts';
import { DATE_TIME_FORMAT_SHORTER } from 'constants/constants';
import { EScheduleTabs } from 'constants/enums';
import { EPendingAppearanceStatus } from 'constants/enums';
import dayjs from 'dayjs';
import { getDurationTextFromMinutes } from 'utils/formattingUtil';
import { statusMapping, statusUtils } from 'utils/scheduleUtils';

import Button from 'components/Button/Button';
import Chip from 'components/Chip/Chip';
import ActionCell from 'components/DataTableComponent/components/ActionCell';
import DataTable from 'components/DataTableComponent/DataTable';
import DatePicker from 'components/DatePicker/DatePicker';
import { DatePickerMode } from 'components/DatePicker/DatePicker.types';
import Input from 'components/Input/Input';
import RichText from 'components/RichText/RichText';
import SwitchSelector from 'components/SwitchSelector/SwitchSelector';
import { Label } from 'components/ui/label';

interface ScheduleTabProps {
  formikProps: FormikProps;
}

const ScheduleTab: React.FC<ScheduleTabProps> = ({ formikProps }) => {
  const { values, setFieldValue } = formikProps;
  const [appearancesTableData, setAppearancesTableData] = useState<
    ScheduleTimeFrame[]
  >([]);
  const [tabTitle, setTabTitle] = useState(
    values?.schedule?.permanent
      ? EScheduleTabs.PERMANENT
      : EScheduleTabs.SCHEDULE
  );
  const [dateRange, setDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined
  });
  const [editedTimeFrame, setEditedTimeFrame] =
    useState<ScheduleTimeFrame | null>(null);
  const [formattedDuration, setFormattedDuration] = useState<string | null>(
    null
  );
  const [notes, setNotes] = useState('');

  const switchOptions = [
    { value: EScheduleTabs.PERMANENT },
    { value: EScheduleTabs.SCHEDULE }
  ];

  useEffect(() => {
    const timeFramesData: ScheduleTimeFrame[] =
      values.schedule?.timeFrames?.map(
        (date: ScheduleTimeFrame, index: number) => ({
          id: date.id || `id-${index}`,
          startTime: date.startTime,
          endTime: date.endTime,
          notes: date.notes
        })
      );

    setAppearancesTableData(timeFramesData);
  }, [values?.schedule?.timeFrames]);

  useEffect(() => {
    if (dateRange.from && dateRange.to) {
      const diffMinutes = dayjs(dateRange.to).diff(
        dayjs(dateRange.from),
        'minute'
      );
      const duration = getDurationTextFromMinutes(diffMinutes);
      setFormattedDuration(duration);
    }
  }, [dateRange]);

  const onClickSwitch = (value: string) => {
    setTabTitle(value as EScheduleTabs);
    setFieldValue('schedule', {
      timeFrames: values?.schedule.timeFrames,
      permanent: value === EScheduleTabs.PERMANENT
    });
  };

  const resetForm = () => {
    setEditedTimeFrame(null);
    setFormattedDuration(null);
    setNotes('');
    setDateRange({ from: undefined, to: undefined });
  };

  const handleApply = (range: DateRange, notes?: string) => {
    const fromUtcDate = range.from;
    const toUtcDate = range.to;

    if (fromUtcDate && toUtcDate) {
      const newTimeFrame: ScheduleTimeFrame = {
        startTime: new Date(fromUtcDate.toISOString()),
        endTime: new Date(toUtcDate.toISOString()),
        notes
      };
      handleAddTimeFrame(newTimeFrame);
      resetForm();
    } else {
      console.error('Invalid date range provided: Start or End date is null.');
    }
  };

  const handleRemoveTimeframe = (startTime: string, endTime: string) => {
    const newTimeFrames = values?.schedule?.timeFrames?.filter(
      (timeFrame: TimeFrameObject) =>
        new Date(timeFrame.startTime).getTime() !==
          new Date(startTime).getTime() &&
        new Date(timeFrame.endTime).getTime() !== new Date(endTime).getTime()
    );

    setFieldValue('schedule', {
      permanent: !newTimeFrames.length ? false : values?.schedule.permanent,
      timeFrames: newTimeFrames
    });
  };

  const handleAddTimeFrame = (newTimeFrame: ScheduleTimeFrame) => {
    if (!newTimeFrame) return;
    const newTimeFramesArray = [
      ...values?.schedule?.timeFrames,
      newTimeFrame
    ].sort(
      (a: ScheduleTimeFrame, b: ScheduleTimeFrame) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );
    setFieldValue('schedule', {
      permanent: values?.schedule.permanent,
      timeFrames: newTimeFramesArray
    });
  };

  const handleEditTimeFrame = (timeframe: ScheduleTimeFrame) => {
    if (!timeframe) return;
    setEditedTimeFrame(timeframe);
    setDateRange({
      from: new Date(timeframe.startTime),
      to: new Date(timeframe.endTime)
    });
    setNotes(timeframe.notes || '');
    handleRemoveTimeframe(
      String(timeframe.startTime),
      String(timeframe.endTime)
    );
  };

  const columns: ColumnDef<ScheduleTimeFrame>[] = [
    {
      accessorKey: 'startTime',
      header: 'Start',
      cell: (cell: CellContext<ScheduleTimeFrame, unknown>) =>
        dayjs(cell.getValue() as string)
          .utc()
          .format(DATE_TIME_FORMAT_SHORTER)
    },
    {
      accessorKey: 'endTime',
      header: 'End',
      cell: (cell: CellContext<ScheduleTimeFrame, unknown>) =>
        dayjs(cell.getValue() as string)
          .utc()
          .format(DATE_TIME_FORMAT_SHORTER)
    },
    {
      accessorKey: 'notes',
      header: 'Notes',
      cell: (cell: CellContext<ScheduleTimeFrame, unknown>) =>
        cell.getValue() as string
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }: { row: Row<ScheduleTimeFrame> }) => {
        const { startTime, endTime, id } = row.original;
        const pendingSaveStatus = id?.startsWith('id-');
        const status = pendingSaveStatus
          ? EPendingAppearanceStatus.PENDING_SAVE
          : statusUtils.determineStatus(new Date(startTime), new Date(endTime));

        return (
          <Chip variant="flat" status={statusMapping[status]}>
            {status}
          </Chip>
        );
      }
    },
    {
      accessorKey: 'actions',
      header: '',
      cell: ({ row }: { row: Row<ScheduleTimeFrame> }) => (
        <ActionCell
          rowData={row.original}
          actions={[
            {
              label: 'Edit',
              handler: () => handleEditTimeFrame(row.original)
            },
            {
              label: 'Delete',
              handler: () =>
                handleRemoveTimeframe(
                  String(row.original.startTime),
                  String(row.original.endTime)
                )
            }
          ]}
        />
      )
    }
  ];

  const tableConfig = {
    data: appearancesTableData,
    showPagination: false,
    columns
  };

  return (
    <div className="w-[528px] mx-auto">
      <RichText
        title="Scheduled Bar"
        description="Set specific dates & hours to your bar"
        className="mb-9"
      />
      <SwitchSelector
        options={switchOptions}
        onClick={onClickSwitch}
        value={tabTitle}
      />
      {tabTitle === EScheduleTabs.SCHEDULE ? (
        <div className="mt-9">
          <DatePicker
            label="Pick a date"
            labelSpacing="mb-2"
            shouldDisplayTime={true}
            shouldExcludePast={true}
            mode={DatePickerMode.RANGE}
            value={dateRange}
            displayInUTC={true}
            onApply={(value: any) => setDateRange(value)}
          />

          {formattedDuration && (
            <div className="space-y-2 mt-5">
              <Label className="text-sm text-zinc-900 font-medium">
                Duration
              </Label>
              <Input value={formattedDuration} disabled />
            </div>
          )}

          <div className="space-y-2 mt-5">
            <Input
              label="Notes"
              name="notes"
              value={notes}
              placeholder="Type your note here"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNotes(e.target.value)
              }
            />
          </div>

          <div className="space-y-3 mt-9">
            <Button
              disabled={!dateRange.from || !dateRange.to}
              onClick={() => handleApply(dateRange, notes)}
              className="w-full bg-base-accent text-base-accentForeground"
              isCreateButton
            >
              {editedTimeFrame !== null ? 'Apply' : 'Add Appearance'}
            </Button>
          </div>

          {!!appearancesTableData.length && (
            <div className="rounded-[20px] mt-6 mb-10 w-full relative left-1/2 transform -translate-x-1/2">
              <DataTable {...tableConfig} />
            </div>
          )}
        </div>
      ) : (
        <div className="mt-8">
          <p className="text-sm text-zinc-500">
            Permanent events won't have an end date
          </p>
        </div>
      )}
    </div>
  );
};

export default ScheduleTab;
